import type { FC } from 'react'

import { useEffect, useState } from 'react'
import { Download } from 'lucide-react'

import { cn } from '@/lib/utils'
import { canInstall, onInstallAvailabilityChange, promptInstall } from '@/lib/pwa'
import { useT } from '@/i18n'

/** Shown only when the browser offers to install the portal as an app. */
/** `icon` renders a round icon-only button for the navbar (`scrolled` picks its colours). */
export const InstallAppButton: FC<{ className?: string; icon?: boolean; scrolled?: boolean }> = ({ className, icon = false, scrolled = false }) => {
    const t = useT()
    const [available, setAvailable] = useState(false)

    useEffect(() => {
        setAvailable(canInstall())
        return onInstallAvailabilityChange(() => setAvailable(canInstall()))
    }, [])

    if (!available) return null

    if (icon) {
        return (
            <button
                type='button'
                onClick={() => void promptInstall()}
                aria-label={t.pwa.install}
                title={t.pwa.install}
                className={cn(
                    'grid size-11 place-items-center rounded-full border transition-colors lg:size-10',
                    scrolled
                        ? 'border-border/70 text-foreground hover:bg-foreground/5'
                        : 'border-parchment/30 text-parchment hover:bg-parchment/10',
                    className
                )}
            >
                <Download className='size-[18px]' />
            </button>
        )
    }

    return (
        <button
            type='button'
            onClick={() => void promptInstall()}
            className={cn(
                'inline-flex w-fit items-center gap-2 rounded-full border border-gold/50 px-4 py-2 text-sm font-medium text-gold-soft transition-colors hover:border-gold hover:bg-gold hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold/70',
                className
            )}
        >
            <Download className='size-4' />
            {t.pwa.install}
        </button>
    )
}
